import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getSelectedOfferPageMultiRoomDay } from 'selectors/offerPage/multipleRoomBookingSelectors'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import TextButton from 'LuxKit/Button/TextButton'
import { getSelectedOfferPageDay } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

interface Props {
  selectedDay?: App.CalendarDay
  onApply: () => void
}

function OfferCalendarConfirmDatesFooter(props: Props) {
  const { selectedDay, onApply } = props

  // Store the selected day locally and persist it until the footer is hidden
  // otherwise we get janky animation when the day disappears and we have no information
  // to show in the footer
  const [localDay, setLocalDay] = useState<App.CalendarDay | undefined>(selectedDay)
  const [initialDay, setInitialDay] = useState<App.CalendarDay | undefined>(selectedDay)

  const hasSelectionChanged = useMemo(() => initialDay?.checkIn !== localDay?.checkIn || initialDay?.checkOut !== localDay?.checkOut, [initialDay, localDay])

  useEffect(() => {
    if (selectedDay) {
      setLocalDay(selectedDay)
    }
  }, [selectedDay])

  const onFooterClose = useCallback<React.TransitionEventHandler<HTMLDivElement>>((e) => {
    if (!selectedDay && e.propertyName === 'transition') {
      setLocalDay(undefined)
      setInitialDay(undefined)
    }
  }, [selectedDay])

  return <FooterContents
    className={clsx({ visible: !!selectedDay && hasSelectionChanged })}
    onTransitionEnd={onFooterClose}
  >
    <ModalFooter>
      <TextButton
        kind="primary"
        size="large"
        type="submit"
        onClick={onApply}
      >
        Confirm new dates
      </TextButton>
    </ModalFooter>
  </FooterContents>
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
  }
}, OfferCalendarConfirmDatesFooter)
